import React from "react";
import _ from "lodash";

import buildTextInputDefinition, {
    TextInputDefinition
} from "../../../../../../../utils/forms/models/grid-fields/text-input-definition";
import buildNumberInputDefinition, {
    NumberInputDefinition
} from "../../../../../../../utils/forms/models/grid-fields/number-input-definition";
import buildSelectMenuDefinition, {
    SelectMenuDefinition
} from "../../../../../../../utils/forms/models/grid-fields/select-menu-definition";
import buildSwitchDefinition, {
    SwitchDefinition
} from "../../../../../../../utils/forms/models/grid-fields/switch-definition";
import buildPasswordDefinition from "../../../../../../../utils/forms/models/grid-fields/password-input-definition";
import { FormDefinition } from "../../../../../../../utils/forms/builders/build-form-grid-fields";

export const CONFIGURATION_TITLE = "Email Sender";

export enum SCHEMA_NAMES {
    ENABLED = "enabled",
    USE_TLS = "use_tls",
    TIMEOUT = "timeout",
    HOST = "host",
    HOST_PORT = "host_port",
    PROTOCOL = "protocol",
    FROM = "from",
    USERNAME = "username",
    PASSWORD = "password",
    BAD = "bad",
    QUEUE = "queue",
    EXPIRED = "expired",
    OUTBOX = "outbox"
}

export const MAIL_SENDER_FIELD_NAMES = {
    [SCHEMA_NAMES.ENABLED]: "Enabled",
    [SCHEMA_NAMES.USE_TLS]: "Use TLS",
    [SCHEMA_NAMES.TIMEOUT]: "Timeout (s)",
    [SCHEMA_NAMES.HOST]: "Host",
    [SCHEMA_NAMES.HOST_PORT]: "Host Port",
    [SCHEMA_NAMES.PROTOCOL]: "Protocol",
    [SCHEMA_NAMES.FROM]: "From",
    [SCHEMA_NAMES.USERNAME]: "Username",
    [SCHEMA_NAMES.PASSWORD]: "Password",
    [SCHEMA_NAMES.BAD]: "Bad",
    [SCHEMA_NAMES.QUEUE]: "Queue",
    [SCHEMA_NAMES.EXPIRED]: "Expired",
    [SCHEMA_NAMES.OUTBOX]: "Outbox"
};

const ENABLED: SwitchDefinition = buildSwitchDefinition({
    name: SCHEMA_NAMES.ENABLED,
    id: SCHEMA_NAMES.ENABLED,
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.ENABLED],
    schemaName: SCHEMA_NAMES.ENABLED
});

const USE_TLS: SwitchDefinition = buildSwitchDefinition({
    name: SCHEMA_NAMES.USE_TLS,
    id: SCHEMA_NAMES.USE_TLS,
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.USE_TLS],
    schemaName: SCHEMA_NAMES.USE_TLS
});

const TIMEOUT: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.TIMEOUT,
    id: SCHEMA_NAMES.TIMEOUT,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.TIMEOUT],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.TIMEOUT],
    schemaName: SCHEMA_NAMES.TIMEOUT
});

const HOST: TextInputDefinition = buildTextInputDefinition({
    name: SCHEMA_NAMES.HOST,
    id: SCHEMA_NAMES.HOST,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.HOST],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.HOST],
    schemaName: SCHEMA_NAMES.HOST
});

const HOST_PORT: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.HOST_PORT,
    id: SCHEMA_NAMES.HOST_PORT,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.HOST_PORT],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.HOST_PORT],
    schemaName: SCHEMA_NAMES.HOST_PORT
});

const PROTOCOL: SelectMenuDefinition = buildSelectMenuDefinition({
    name: SCHEMA_NAMES.PROTOCOL,
    id: SCHEMA_NAMES.PROTOCOL,
    noValueLabel: "Select an option...",
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.PROTOCOL],
    items: [
        {
            label: "SMTP",
            value: "smtp"
        },
        {
            label: "SMTPS",
            value: "smtps"
        }
    ],
    schemaName: SCHEMA_NAMES.PROTOCOL
});

const FROM: TextInputDefinition = buildTextInputDefinition({
    name: SCHEMA_NAMES.FROM,
    id: SCHEMA_NAMES.FROM,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.FROM],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.FROM],
    schemaName: SCHEMA_NAMES.FROM
});

const USERNAME: TextInputDefinition = buildTextInputDefinition({
    name: SCHEMA_NAMES.USERNAME,
    id: SCHEMA_NAMES.USERNAME,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.USERNAME],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.USERNAME],
    schemaName: SCHEMA_NAMES.USERNAME
});

const PASSWORD: TextInputDefinition = buildPasswordDefinition({
    name: SCHEMA_NAMES.PASSWORD,
    id: SCHEMA_NAMES.PASSWORD,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.PASSWORD],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.PASSWORD],
    schemaName: SCHEMA_NAMES.PASSWORD
});

const BAD: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.BAD,
    id: SCHEMA_NAMES.BAD,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.BAD],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.BAD],
    schemaName: SCHEMA_NAMES.BAD
});

const QUEUE: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.QUEUE,
    id: SCHEMA_NAMES.QUEUE,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.QUEUE],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.QUEUE],
    schemaName: SCHEMA_NAMES.QUEUE
});

const EXPIRED: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.EXPIRED,
    id: SCHEMA_NAMES.EXPIRED,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.EXPIRED],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.EXPIRED],
    schemaName: SCHEMA_NAMES.EXPIRED
});

const OUTBOX: NumberInputDefinition = buildNumberInputDefinition({
    name: SCHEMA_NAMES.OUTBOX,
    id: SCHEMA_NAMES.OUTBOX,
    placeholder: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.OUTBOX],
    label: MAIL_SENDER_FIELD_NAMES[SCHEMA_NAMES.OUTBOX],
    schemaName: SCHEMA_NAMES.OUTBOX
});

export const MAIL_SENDER_FORM_DEFINITION: () => FormDefinition = () => {
    let fields = {
        [SCHEMA_NAMES.ENABLED]: {
            definition: {
                ...ENABLED,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 0
        },
        [SCHEMA_NAMES.USE_TLS]: {
            definition: {
                ...USE_TLS,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 0
        },
        [SCHEMA_NAMES.TIMEOUT]: {
            definition: {
                ...TIMEOUT,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 4
        },
        [SCHEMA_NAMES.HOST]: {
            definition: {
                ...HOST,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 2
        },
        [SCHEMA_NAMES.HOST_PORT]: {
            definition: {
                ...HOST_PORT,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 3
        },
        [SCHEMA_NAMES.PROTOCOL]: {
            definition: {
                ...PROTOCOL,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 1
        },
        [SCHEMA_NAMES.FROM]: {
            definition: {
                ...FROM,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 5
        },
        [SCHEMA_NAMES.USERNAME]: {
            definition: {
                ...USERNAME,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 6
        },
        [SCHEMA_NAMES.PASSWORD]: {
            definition: {
                ...PASSWORD,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 7
        },
        [SCHEMA_NAMES.BAD]: {
            definition: {
                ...BAD,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 8
        },
        [SCHEMA_NAMES.QUEUE]: {
            definition: {
                ...QUEUE,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 9
        },
        [SCHEMA_NAMES.EXPIRED]: {
            definition: {
                ...EXPIRED,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 10
        },
        [SCHEMA_NAMES.OUTBOX]: {
            definition: {
                ...OUTBOX,
                fieldOverrides: {
                    formSpacing: false,
                    columnClass: "col-xs-12 col-sm-6 col-md-4 col-lg-3 col-xl-3"
                }
            },
            order: 11
        }
    };

    return fields;
};
